const MAX_CONNECTORS_LABEL_LENGTH = 50;

/**
 * Truncates a given string to fit within a specified maximum length. If the string exceeds the maximum length,
 * it is shortened, and an ellipsis (`...`) is inserted in the middle to indicate that the string has been truncated.
 * The resulting string will preserve a portion of the beginning and end of the original string.
 *
 * @param input - The string to be truncated.
 * @param maxStringLength - The maximum length of the resulting string, including the ellipsis. Defaults to `MAX_CONNECTORS_LABEL_LENGTH`.
 *                           If this substring max is equal to 0, the function will return the input string unmodified.
 * @returns The truncated string if it exceeds the maximum length; otherwise, the original string.
 *
 * @example
 * const MAX_CONNECTORS_LABEL_LENGTH = 10;
 *
 * console.log(truncateString("Hello, world!")); // Output: "Hello...orld!"
 * console.log(truncateString("Short text")); // Output: "Short text"
 * console.log(truncateString("This is a very long string", 20)); // Output: "This is a ve...tring"
 */
export function truncateString(
  input: string,
  maxStringLength = MAX_CONNECTORS_LABEL_LENGTH,
): string {
  const maxSubstringLength = maxStringLength / 2 - 3;

  if (maxSubstringLength === 0 || input.length <= maxStringLength) return input;

  return `${input.substring(0, maxSubstringLength)}…${input.substring(
    input.length - maxSubstringLength,
    input.length,
  )}`;
}

/**
 * Formats a connector label string into a multi-line format for improved readability.
 * If the label length exceeds the maximum allowed length, it splits the label into its components
 * and truncates the URL path and query parameters to fit within the maximum length.
 * The formatted label includes the connector name, type, method, and a truncated URL.
 *
 * @param label - The connector label string to be formatted. The label is expected to contain
 *                at least four components separated by spaces: connectorName, type, method, and url.
 *                The URL can optionally include query parameters.
 * @returns A formatted string with each component on a new line. If the label is shorter than
 *          or equal to `MAX_CONNECTORS_LABEL_LENGTH`, it is returned as-is.
 *          The URL path and query parameters are truncated if necessary to fit within the maximum length.
 *
 * @example
 * const label = "MyConnector http POST: https://api.example.com/path?query=param";
 * console.log(formatConnectorsLabel(label));
 * // Output:
 * // MyConnector
 * // http POST: https://api.example.com/path
 * // ?query=param
 *
 * @example
 * const shortLabel = "ShortConnector http POST: /endpoint";
 * console.log(formatConnectorsLabel(shortLabel));
 * // Output:
 * // ShortConnector
 * // http POST: /endpoint
 */
export function formatConnectorsLabel(label: string): string {
  if (label.length <= MAX_CONNECTORS_LABEL_LENGTH) return label;

  const parts = label.split(' ');
  const [connectorName, type, method] = parts;
  const url = parts.slice(3).join(' ');
  const [path, queryParams] = url.split('?');

  return `${connectorName}
${[type, method].join(' ')} ${truncateString(path)}${
    queryParams ? `\n${truncateString(`?${queryParams}`)}` : ''
  }`.replaceAll('"', '&quot;');
}
